import { currentLocale } from "../locales";
import { PermissionType } from "../types/users";

type LocalizedLabel = { en: string; gu: string };

const permissionModule: Record<string, LocalizedLabel> = {
  auth: { en: "Auth", gu: "લોગિન" },
  dashboard: { en: "Dashboard", gu: "ડેશબોર્ડ" },
  users: { en: "Users", gu: "યુઝર્સ" },
  parties: { en: "Parties", gu: "પાર્ટી" },
  items: { en: "Items", gu: "આઇટમ" },
  processes: { en: "Sub-Processes", gu: "સબ પ્રોસેસ" },
  "process-definitions": { en: "Processes", gu: "પ્રોસેસ" },
  sizes: { en: "Sizes", gu: "સાઇઝ" },
  "item-sizes": { en: "Item Sizes", gu: "આઇટમ સાઇઝ" },
  "account-types": { en: "Account Types", gu: "ખાતા પ્રકાર" },
  accounts: { en: "Accounts", gu: "ખાતા" },
  "process-orders": { en: "Process Orders", gu: "પ્રોસેસ ઓર્ડર" },
  "process-receives": { en: "Process Receives", gu: "પ્રોસેસ રિસીવ" },
  "sizes-stock": { en: "Sizes Stock", gu: "સાઈઝ સ્ટોક" },
  "pieces-stock": { en: "Pipe Pieces Stock", gu: "પાઇપ પીસ સ્ટોક" },
  "process-items-stock": { en: "Process Items Stock", gu: "પ્રોસેસ આઇટમ સ્ટોક" },
  "process-party-stock": { en: "Process Party Stock", gu: "પ્રોસેસ પાર્ટી સ્ટોક" },
  "money-view": { en: "Money / Amounts", gu: "રકમ" },
  settings: { en: "Settings", gu: "સેટિંગ્સ" },
  "activity-logs": { en: "Activity Logs", gu: "પ્રવૃત્તિ લૉગ્સ" },
};

const permissionAction: Record<string, LocalizedLabel> = {
  menu: { en: "Menu", gu: "મેનુ" },
  read: { en: "Read", gu: "જોવા" },
  create: { en: "Create", gu: "બનાવવા" },
  update: { en: "Update", gu: "સંપાદિત કરવા" },
  delete: { en: "Delete", gu: "કાઢી નાખવા" },
  payment: { en: "Payment", gu: "પેમેન્ટ" },
  rate: { en: "Rate", gu: "દર" },
  login: { en: "Login", gu: "લોગિન" },
  profile: { en: "Profile", gu: "પ્રોફાઇલ" },
};

export const getPermissionModule = (en: string): string => {
  const entry = permissionModule[en?.toLowerCase()];
  if (!entry) return en;
  return entry[currentLocale] ?? en;
};

export const getPermissionAction = (en: string): string => {
  const entry = permissionAction[en?.toLowerCase()];
  if (!entry) return en;
  return entry[currentLocale] ?? en;
};

export const getGujaratiPermissionModule = getPermissionModule;
export const getGujaratiPermissionAction = getPermissionAction;

export const MODULE_ORDER: string[] = [
  "auth",
  "dashboard",
  "settings",
  "activity-logs",
  "users",
  "parties",
  "items",
  "processes",
  "process-definitions",
  "sizes",
  "item-sizes",
  "process-orders",
  "process-receives",
  "account-types",
  "accounts",
  "sizes-stock",
  "pieces-stock",
  "process-items-stock",
  "process-party-stock",
  "money-view",
];

export const ACTION_ORDER: string[] = [
  "menu",
  "read",
  "create",
  "update",
  "delete",
];

export const MODULE_ACTION_ORDER: Record<string, string[]> = {
  parties: ["menu", "read", "create", "update", "delete", "payment", "rate"],
  auth: ["login", "profile"],
};

export function sortPermissionsByModuleAndAction(
  permissions: PermissionType[]
): Record<string, PermissionType[]> {
  const moduleIndex = new Map(MODULE_ORDER.map((m, i) => [m, i]));
  const actionIndexFor = (mod: string) => {
    const list = MODULE_ACTION_ORDER[mod] ?? ACTION_ORDER;
    return new Map(list.map((a, i) => [a, i]));
  };
  const rank = (idx: Map<string, number>, key: string | null) =>
    key && idx.has(key) ? (idx.get(key) as number) : Number.POSITIVE_INFINITY;

  const grouped: Record<string, PermissionType[]> = {};
  for (const p of permissions) {
    (grouped[p.module] ||= []).push(p);
  }

  const sortedModules = Object.keys(grouped).sort((a, b) => {
    const ra = rank(moduleIndex, a);
    const rb = rank(moduleIndex, b);
    if (ra !== rb) return ra - rb;
    return a.localeCompare(b);
  });

  const out: Record<string, PermissionType[]> = {};
  for (const mod of sortedModules) {
    const idx = actionIndexFor(mod);
    out[mod] = [...grouped[mod]].sort((x, y) => {
      const rx = rank(idx, x.action);
      const ry = rank(idx, y.action);
      if (rx !== ry) return rx - ry;
      return (x.action ?? x.name).localeCompare(y.action ?? y.name);
    });
  }
  return out;
}
